import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, CheckCircle2, KeyRound } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const { resetPassword } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) {
      setError("Missing or invalid reset token.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    const res = await resetPassword(token, password);
    setLoading(false);
    if (res.ok) {
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 1800);
    } else {
      setError(res.error || "Failed to reset password.");
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F7F9FB] px-6 py-10" data-testid="reset-password-page">
      <div className="flex-1 flex flex-col justify-center max-w-md w-full mx-auto">
        {!done ? (
          <>
            <div className="w-14 h-14 rounded-2xl bg-[#EAF6F4] flex items-center justify-center mb-4">
              <KeyRound size={26} className="text-[#4FB3A5]" />
            </div>
            <h1 className="text-3xl font-bold text-[#1F3A5F] mb-2">Set a new password</h1>
            <p className="text-[#6B7280] mb-8">Choose a strong password you'll remember.</p>

            <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-[#2C2C2C]">New password</label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className="h-12 rounded-xl bg-white"
                  data-testid="reset-password-input"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-[#2C2C2C]">Confirm password</label>
                <Input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="h-12 rounded-xl bg-white"
                  data-testid="reset-password-confirm"
                />
              </div>

              {error && (
                <div className="text-sm text-[#E07A7A] bg-[#FEF3F2] border border-[#FECACA] p-3 rounded-xl">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 rounded-xl bg-[#1F3A5F] hover:bg-[#2a4a75] text-white font-semibold"
                data-testid="reset-submit-btn"
              >
                {loading ? <Loader2 className="animate-spin" /> : "Reset password"}
              </Button>
            </form>
          </>
        ) : (
          <div className="text-center" data-testid="reset-success">
            <div className="w-16 h-16 bg-[#EAF6F4] rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 size={32} className="text-[#4FB3A5]" />
            </div>
            <h2 className="text-2xl font-bold text-[#1F3A5F] mb-2">Password updated</h2>
            <p className="text-[#6B7280] mb-6">Redirecting you to login…</p>
            <Link to="/login">
              <Button className="w-full h-12 rounded-xl bg-[#1F3A5F] hover:bg-[#2a4a75] text-white font-semibold">
                Log in now
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
